import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Sparkles, Type, Image as ImageIcon, Euro, TrendingUp, TrendingDown } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

// Libellés lisibles pour les fonctionnalités IA journalisées.
const FEATURE_LABELS = {
  blog: "Blog local",
  studio_ia: "Studio IA",
  reseaux_sociaux: "Réseaux sociaux",
  pub_facebook_pro: "Pubs Facebook (pros)",
  seo: "SEO / référencement",
  autre: "Autre",
};

const eur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// Construit la liste des 12 derniers mois (format YYYY-MM) pour le sélecteur.
function lastMonths(count = 12) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < count; i++) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    out.push({ value: `${y}-${m}`, label: d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

function Kpi({ icon: Icon, label, value, sub, accent = "#E05638" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`ai-cost-kpi-${label}`}>
      <div className="text-xs uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
        <Icon size={13} style={{ color: accent }} /> {label}
      </div>
      <div className="mt-1 text-3xl font-extrabold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export default function AdminAICosts() {
  const months = lastMonths(12);
  const [month, setMonth] = useState(months[0].value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/ai-usage?month=${month}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [month]);

  const text = data?.by_kind?.text || { count: 0, cost_eur: 0 };
  const image = data?.by_kind?.image || { count: 0, cost_eur: 0 };
  const prev = data?.previous_month || { total_cost_eur: 0, total_count: 0 };
  const total = data?.total_cost_eur || 0;
  const diff = total - (prev.total_cost_eur || 0);
  const diffPct = prev.total_cost_eur > 0 ? Math.round((diff / prev.total_cost_eur) * 100) : null;
  const featTotal = (data?.by_feature || []).reduce((s, f) => s + f.cost_eur, 0) || 1;
  const maxDay = Math.max(1, ...((data?.by_day || []).map((d) => d.cost_eur)));

  return (
    <div data-testid="admin-ai-costs-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 flex items-center gap-2">
            <Sparkles size={22} className="text-[#E05638]" /> Suivi des coûts IA
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Estimation des dépenses de génération IA (texte GPT-6 Luna &amp; images Nano Banana) pour le contrôle du budget.
          </p>
        </div>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          data-testid="ai-cost-month-selector"
          className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#E05638]/30"
        >
          {months.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Chargement…</div>
      ) : !data ? (
        <div className="mt-6 p-8 bg-white border border-slate-200 rounded-2xl text-center text-slate-400">
          Impossible de charger les données.
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={Euro} label="Coût total du mois" value={eur(total)} sub={`${data.total_count} génération(s)`} />
            <Kpi icon={Type} label="Texte" value={eur(text.cost_eur)} sub={`${text.count} génération(s)`} accent="#1877F2" />
            <Kpi icon={ImageIcon} label="Images" value={eur(image.cost_eur)} sub={`${image.count} génération(s)`} accent="#8b5cf6" />
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="ai-cost-kpi-prev">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
                {diff >= 0 ? <TrendingUp size={13} className="text-rose-500" /> : <TrendingDown size={13} className="text-emerald-600" />} vs mois précédent
              </div>
              <div className={`mt-1 text-3xl font-extrabold ${diff >= 0 ? "text-rose-600" : "text-emerald-600"}`}>
                {diff >= 0 ? "+" : "−"}{eur(Math.abs(diff))}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {eur(prev.total_cost_eur)} en {prev.month}{diffPct !== null ? ` · ${diff >= 0 ? "+" : ""}${diffPct}%` : ""}
              </div>
            </div>
          </div>

          {/* Évolution par jour */}
          <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="ai-cost-daily-chart">
            <div className="font-heading font-extrabold text-slate-900 mb-4">Coût par jour</div>
            {(data.by_day || []).length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">Aucune génération ce mois-ci.</div>
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <AreaChart data={data.by_day} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gaicost" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#E05638" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#E05638" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(d) => (d || "").slice(8)} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `${v}€`} />
                    <Tooltip formatter={(v) => eur(v)} labelFormatter={(d) => `Le ${d}`} />
                    <Area type="monotone" dataKey="cost_eur" name="Coût" stroke="#E05638" strokeWidth={2} fill="url(#gaicost)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Répartition par fonctionnalité */}
          <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="ai-cost-by-feature">
            <div className="font-heading font-extrabold text-slate-900 mb-4">Coût par fonctionnalité</div>
            {(data.by_feature || []).length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">Aucune donnée.</div>
            ) : (
              <div className="space-y-3">
                {data.by_feature.map((f, i) => {
                  const pct = Math.round((f.cost_eur / featTotal) * 100);
                  return (
                    <div key={f.feature} className="flex items-center gap-3" data-testid={`ai-cost-feature-${f.feature}`}>
                      <div className="w-40 text-xs font-semibold text-slate-600 shrink-0 truncate">
                        {FEATURE_LABELS[f.feature] || f.feature}
                        <span className="text-slate-400 font-normal"> · {f.count}</span>
                      </div>
                      <div className="flex-1 h-5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: ["#E05638", "#1877F2", "#8b5cf6", "#25D366", "#f0a23a", "#94a3b8"][i % 6], minWidth: f.cost_eur ? 6 : 0 }} />
                      </div>
                      <div className="w-24 text-right text-xs font-bold text-slate-900">{eur(f.cost_eur)}</div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-400">
              Tarifs unitaires estimés : texte {eur(data.unit_cost_eur?.text)} / image {eur(data.unit_cost_eur?.image)} par génération. Montants indicatifs.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
